import React from "react";
import { Box, TextField } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import type { Job } from "../../../../../../../models";
import { ImportTableColumn } from "../../../../../../../utilities/types";
import {
	ValidationContext,
	isFieldRequired,
	getValidationErrorKey,
} from "../../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../../admin/ImportSettings";
import { ColumnContext } from "../ColumnContext";
import { DateColumnOptions, DateColumnViewModel } from "./dateColumn.types";
import { useDateColumnViewModel } from "./dateColumn.vm";
import { useValidationErrors } from "../../../shared/hooks/ValidationErrorsContext";
import { useIsRowInEditMode, useEditModeContext } from "../../../shared/hooks/EditModeContext";

interface DateColumnCellProps {
	row: MRT_Row<Job>;
	vm: DateColumnViewModel;
	fieldKey: keyof Job;
	columnId: ImportTableColumn;
	isRequiredOption?: boolean;
	getRowValidationContext: (row: MRT_Row<Job>) => ValidationContext;
}

/**
 * Memoized Cell component for date columns
 */
const DateColumnCell = React.memo(
	function DateColumnCell({
		row,
		vm,
		fieldKey,
		columnId,
		isRequiredOption = false,
		getRowValidationContext,
	}: DateColumnCellProps) {
		const job = row.original;
		const validationErrors = useValidationErrors();
		const isInEditMode = useIsRowInEditMode(job.id);
		const { exitRowEdit } = useEditModeContext();
		const currentValue = vm.getCurrentDateValue(job, fieldKey);
		const errorKey = getValidationErrorKey(columnId, job.id);
		const rowContext = getRowValidationContext(row);
		const isRequired = typeof isRequiredOption === "boolean" ? isRequiredOption : isFieldRequired(columnId, rowContext);
		const hasError = !!validationErrors?.[errorKey];

		// View Mode: render formatted date string for performance
		if (!isInEditMode) {
			// Format the date for display (from YYYY-MM-DD to more readable format)
			let displayValue = "";
			if (currentValue) {
				try {
					const date = new Date(currentValue + "T00:00:00"); // Add time to avoid timezone issues
					displayValue = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
				} catch {
					displayValue = currentValue;
				}
			}
			return (
				<Box
					title={hasError ? validationErrors[errorKey] : undefined}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: hasError ? "error.main" : "inherit",
						borderBottom: hasError ? "2px solid" : "none",
						borderColor: "error.main",
						width: "100%",
						px: 0.5,
					}}
				>
					{displayValue || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		return (
			<TextField
				size="small"
				type="date"
				variant="outlined"
				required={isRequired}
				fullWidth
				value={currentValue}
				error={hasError}
				helperText={validationErrors?.[errorKey]}
				onChange={(event) => {
					vm.handleDateChange(job.id, fieldKey, event.target.value);
				}}
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						exitRowEdit(job.id);
					}
				}}
				onBlur={() => {
					vm.handleDateBlur(job, fieldKey, row);
				}}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob[prevProps.fieldKey] === nextJob[nextProps.fieldKey] &&
			prevProps.columnId === nextProps.columnId &&
			prevProps.isRequiredOption === nextProps.isRequiredOption
		);
	},
);

interface DateColumnAggregatedCellProps {
	row: MRT_Row<Job>;
	vm: DateColumnViewModel;
	fieldKey: keyof Job;
}

/**
 * Memoized AggregatedCell component for date columns
 * Renders editable TextField when group row is in edit mode
 */
const DateColumnAggregatedCell = React.memo(function DateColumnAggregatedCell({
	row,
	vm,
	fieldKey,
}: DateColumnAggregatedCellProps) {
	// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
	const isInEditMode = useIsRowInEditMode(row.id);
	const { exitRowEdit } = useEditModeContext();

	// Get value from first job in group
	const job = row.original;
	const currentValue = vm.getCurrentDateValue(job, fieldKey);
	const groupValue = job.importGrowerName || "";

	// View Mode: render formatted date string
	if (!isInEditMode) {
		let displayValue = "";
		if (currentValue) {
			try {
				const date = new Date(currentValue + "T00:00:00");
				displayValue = date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
			} catch {
				displayValue = currentValue;
			}
		}
		return (
			<Box
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					width: "100%",
					px: 0.5,
				}}
			>
				{displayValue || <span style={{ color: "#999" }}>-</span>}
			</Box>
		);
	}

	// Edit Mode: render TextField that updates all rows in group on change
	return (
		<TextField
			size="small"
			type="date"
			variant="outlined"
			fullWidth
			value={currentValue}
			onChange={(event) => {
				vm.handleGroupDateChange(groupValue, fieldKey, event.target.value);
			}}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					exitRowEdit(row.id);
				}
			}}
		/>
	);
});

/**
 * Build a date picker column with controlled input
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function dateColumn(
	context: ColumnContext,
	fieldKey: keyof Job,
	columnId: ImportTableColumn,
	options?: DateColumnOptions,
): MRT_ColumnDef<Job, unknown> {
	const vm = useDateColumnViewModel(context);

	const { isRequired: isRequiredOption = false, size, hasGroupUpdate = true } = options || {};

	const displayName = columnDisplayNames[columnId];

	return {
		id: fieldKey as string,
		header: displayName,
		accessorKey: fieldKey as string,
		size,
		enableEditing: false,
		Cell: ({ row }: { row: MRT_Row<Job> }) => (
			<DateColumnCell
				row={row}
				vm={vm}
				fieldKey={fieldKey}
				columnId={columnId}
				isRequiredOption={isRequiredOption}
				getRowValidationContext={context.getRowValidationContext}
			/>
		),
		...(hasGroupUpdate && {
			AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => (
				<DateColumnAggregatedCell row={row} vm={vm} fieldKey={fieldKey} />
			),
		}),
	};
}
